import React, { useState, useEffect } from 'react'
import { 
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, FormGroup, Input, Row, Col, Label,
  UncontrolledTooltip
} from 'reactstrap'
import { Accordion, Card } from 'react-bootstrap'
import Frame from 'react-frame-component'
import { IoMdRefresh } from 'react-icons/io'
import _ from 'lodash'

import Lang from 'Lang/General'
import ColorPickerInput from 'components/ColorPicker'
import MultilingualInput from 'components/Input/MultiLang'
import { getBorderStyling, getButtonColorType, getModifiedColorTypeStyling } from 'components/ButtonSettings/utils'
import { patchIdsChildren } from 'utils/uniqueIds'
import CoreRenderFunction from 'containers/TemplateEditor/core/render'
import LinkInput from 'components/LinkInput'

const newButton = {
  id: "",
  type: "button",
  style: {
    color: "#ffffff",
    background: "#238acc",
    border: "none",
    borderRadius: ".25rem",
    outline: "none",
    padding: "5px 8px",
    transition: "0.3s linear all",
    ':hover': {
      color: "#000000",
      background: "#ffffff",
      border: "1px solid #000000"
    }
  },
  children: [{
    id: "",
    type: "div",
    variant: "text",
    style: { zIndex: 2, position: "relative" },
    content: Lang[ 'SUBMIT' ]
  }]
}

const ButtonConfigModal = props => {
  let {
    showSelectButtonModal,
    selectedLanguage,
    components,
    selectedTempComponent,
    onChangeTempComponent,
    toggleMediaDatabase,
    redirectInfo,
    onChangeRedirectInfo
  } = props

  const [ bottomButtonText, updateBottomButtonText ] = useState( Lang[ 'SUBMIT' ] )
  const [ selectedButtonTemplate, updateSelectedButtonTemplate ] = useState( _.cloneDeep( newButton ) )

  useEffect(() => {
    if( showSelectButtonModal ){
      if( _.isEmpty( selectedTempComponent ) ){
        let tmp = _.cloneDeep( newButton )
        patchIdsChildren( tmp )
        onChangeTempComponent( 'selectedTempComponent', tmp )
      } else {
        let tmp = _.find( components, component => component.content.className === selectedTempComponent.className )
        updateSelectedButtonTemplate( tmp ? tmp.content : newButton )
        if( !_.isEmpty( selectedTempComponent.content )){
          updateBottomButtonText( selectedTempComponent.content )
        } else if( selectedTempComponent.children?.length > 0 ) {
          updateBottomButtonText( selectedTempComponent.children[0].content )
        }
      }
    }
  }, [ showSelectButtonModal ])

  const onChangeButtonStyle = ( key, val, isHover ) => {
    let tmp = _.cloneDeep( selectedTempComponent )
    if( isHover ){
      if( !tmp.style[ ':hover' ] ){
        tmp.style[ ':hover' ] = {}
      }
      if( key === "border" ){
        let tmpBorder = getBorderStyling( tmp.style[ ':hover' ] )
        if( Object.keys( tmpBorder ).length > 0 ){
          _.map( ( tmpBorder ), ( value, key ) => {
            tmp.style[ ':hover' ][ key ] = value.replace( /#[^ ]*\b/g, val )
          })
        }
      } else {
        if( key === "outline" && !tmp.style[ ':hover' ][ key ]?.indexOf( 'px' ) > -1 ){
          tmp.style[ ':hover' ][ key ] = `1px solid ${ val }`
        } else {
          tmp.style[ ':hover' ][ key ] = val
        }
      }
    } else {
      if( key === "border" ){
        let tmpBorder = getBorderStyling( tmp.style )
        if( Object.keys( tmpBorder ).length > 0 ){
          _.map( ( tmpBorder ), ( value, key ) => {
            tmp.style[ key ] = value.replace( /#[^ ]*\b/g, val )
          })
        }
      } else {
        if( key === "outline" && !tmp.style[ key ]?.indexOf( 'px' ) > -1 ){
          tmp.style[ key ] = `1px solid ${ val }`
        } else {
          tmp.style[ key ] = val
        }
      }
    }
    onChangeTempComponent( "selectedTempComponent", tmp )
  }

  const onChangeButtonColorType = ( type, key, isHover ) => {
    let tmp = _.cloneDeep( selectedTempComponent )
    let tmpStyle = {}

    if( isHover ){
      if( !tmp.style[ ':hover' ] ){
        tmp.style[ ':hover' ] = {}
      }
    }
    tmpStyle = _.cloneDeep( isHover ? tmp.style[ ':hover' ] : tmp.style )

    if( !tmpStyle[ key ] && key !== 'border' ){
      tmpStyle[ key ] = "#000000"
    }
    if( key === "border" ){
      _.map( getBorderStyling( isHover ? tmp.style[ ':hover' ] : tmp.style ), ( value, borderKey ) => {
        tmpStyle = getModifiedColorTypeStyling( type, tmpStyle, borderKey )
      })
    } else {
      if( !tmpStyle[ key ] ){
        if( key === "outline" ){
          tmpStyle[ key ] = '1px solid #000000'
        } else {
          tmpStyle[ key ] = "#000000"
        }
      }
      tmpStyle = getModifiedColorTypeStyling( type, tmpStyle, key )
    }

    if( isHover ){
      tmp.style[ ':hover' ] = tmpStyle
    } else {
      tmp.style = tmpStyle
    }
    onChangeTempComponent( "selectedTempComponent", tmp )
  }

  const ColorPicker = ( attr, isHover ) => {
    let tmpStyle = isHover 
      ? selectedTempComponent.style[ ':hover' ]
        ? selectedTempComponent.style[ ':hover' ]
        : {}
      : selectedTempComponent.style
    let isReplace = attr !== "border" ? tmpStyle[ attr ]?.indexOf( '#' ) > -1 : false
    let attrColor = attr === "border" 
      ? Object.values( getBorderStyling( tmpStyle ) )[ 0 ]?.match( /#[^ ]*\b/g )[ 0 ] || ''
      : isReplace 
        ? tmpStyle[ attr ].match( /#[^ ]*\b/g )[0]
        : ""
    return(
      <ColorPickerInput
        id={ `button_${ attr }` }
        size={ 6 }
        label={ Lang[ attr === "backgroundColor" ? "BACKGROUND" : attr.toUpperCase() ][ selectedLanguage ] }
        color={ attrColor }
        colorType={ getButtonColorType( 
          tmpStyle, 
          attr === "border" 
            ? Object.keys( getBorderStyling( tmpStyle ) )[ 0 ] 
            : attr 
        )}
        selectedLanguage={ selectedLanguage }
        onChange={ col => {
          onChangeButtonStyle(
            attr, 
            isReplace ? tmpStyle[ attr ].replace( /#[^ ]*\b/g, col.hex ) : col.hex, 
            isHover
          )
        }}
        onChangeColorType={ type => onChangeButtonColorType( type, attr, isHover ) }
        enableAttrRemoval
      />
    )
  }

  return (
    <Modal isOpen={ showSelectButtonModal } size={ 'lg' }>
      <ModalHeader toggle={() => onChangeTempComponent( 'showSelectButtonModal', false )}>
        { Lang[ 'BUTTON_CONFIGURATION' ][ selectedLanguage ] }
      </ModalHeader>
      { 
        !_.isEmpty( selectedTempComponent ) && (
          <ModalBody>
            <Frame
              id={ 'editor-section-renderer' }
              style={{
                width: '100%',
                height: '75px',
                marginBottom: '20px'
              }}
              initialContent={ '<!DOCTYPE html><html><head><link href="/bootstrap.css" rel="stylesheet"></link><link href="/image-block-styles.css" rel="stylesheet"></link></head><body style="overflow-y:scroll;overflow-x:hidden;"><div></div></body></html>' }>
              <div
                style={{
                  width: '100%',
                  height: '100vh',
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <style>{`.left-right-animated-button:after { content: ''; position: absolute; top: 0; left: 0; width: 0; height: 100%; z-index: 1; opacity: 1; background-color: #252525; transition: 0.4s; }  .left-right-animated-button:hover:after { width: 100%; } \n.left-right-animated-button:hover div { color: #ffffff !important; }  \n\n.left-right-reverse-animated-button:after { content: ''; position: absolute; top: 0; left: 0; width: 0; height: 100%; z-index: 1; opacity: 1; background-color: #ffffff; transition: 0.4s; }  \n.left-right-reverse-animated-button:hover:after { width: 100%; } \n.left-right-reverse-animated-button:hover div { color: #252525 !important; } \n\n.left-right-animated-button-v2:after { content: ''; position: absolute; top: 0; left: 0; width: 0; height: 100%; z-index: 1; opacity: 1; background-color: #252525; transition: 0.4s; } \n.left-right-animated-button-v2:hover:after { width: 100%; }\n\n.left-right-reverse-animated-button-v2:after { content: ''; position: absolute; top: 0; left: 0; width: 0; height: 100%; z-index: 1; opacity: 1; background-color: #ffffff; transition: 0.4s; } \n.left-right-reverse-animated-button-v2:hover:after { width: 100%; }\n\n.border-top-animated-button:before { content: ''; display: block; position: absolute; top: 100%; left: 0; width: 100%; height: 2px; transform: scale(0.85); background: #252525; transition: all 0.3s ease; }\n.border-top-animated-button:hover:before { transform: scale(1); } \n.border-top-animated-button:after { content: ''; display: block; position: absolute; top: 100%; left: 0; width: 100%; height: 2px; transform: scale(0.85); background: #252525; transition: all 0.3s ease; }\n.border-top-animated-button:hover:after { transform: scale(1); top: 0; } \n \n.animated-important-button:before { content: ''; background-color: #ad0a0a; width: 0; height: 100%; position: absolute; top: 0; left: 0; z-index: 1;  transition: 0.4s; }\n.animated-important-button:hover:before { width: 100%; }\n\n.outline-animated-button:before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; opacity: 0; transform: scale(1,0.5); background-color: #808080; transition: 0.4s; }\n.outline-animated-button:hover:before { opacity: 1; transform: scale(1); }\n\n.outline-animated-secondary-button:before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; opacity: 0; transform: scale(1,0.5); background-color: #808080; transition: 0.4s; }\n.outline-animated-secondary-button:hover:before { opacity: 1; transform: scale(1); }\n\n.slant-animated-button:before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; opacity: 0; transform: scale(0.5,1); background-color: #efefef; transition: 0.4s; } \n.slant-animated-button:hover:before { opacity: 1; transform: scale(1); }\n\n.slant-animated-primary-button:before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; opacity: 0; transform: scale(0.5,1); background-color: #454545; transition: 0.4s; } \n.slant-animated-primary-button:hover:before { opacity: 1; transform: scale(1); }`}</style>
                <CoreRenderFunction
                  item={ selectedTempComponent }
                  selectedLanguageShortname={ selectedLanguage }
                  mode={ 'section-display-edit' }/>
              </div>
            </Frame>
            <FormGroup>
              <Label>{ Lang[ 'BUTTON_TYPE' ][ selectedLanguage ] }</Label>
              <Input
                type="select"
                value={ selectedTempComponent.className }
                onChange={ e => {
                  if( e.target.value !== "" ){
                    let tmp = _.cloneDeep( _.find( components, component => component.content.className === e.target.value ).content )
                    updateSelectedButtonTemplate( _.cloneDeep( tmp ) )
                    tmp.children[0].content = bottomButtonText
                    patchIdsChildren( tmp )
                    onChangeTempComponent( 'selectedTempComponent', tmp )
                  } else {
                    let tmp = _.cloneDeep( newButton )
                    updateSelectedButtonTemplate( newButton )
                    patchIdsChildren( tmp )
                    onChangeTempComponent( 'selectedTempComponent', tmp )
                  }
                }}>
                <option value="">{ Lang[ 'DEFAULT' ][ selectedLanguage ] }</option>  
                {
                  components.map( component => {
                    return <option value={ component.content.className }>{ component.name }</option>
                  })
                }
              </Input>
            </FormGroup>
            <Row>
              <Col lg={ 5 } style={{ marginBottom: "10px" }}>
                <MultilingualInput
                  label={ Lang[ 'TEXT' ][ selectedLanguage ] }
                  selectedLanguage={ selectedLanguage }
                  value={ bottomButtonText }
                  onChange={ val => {
                    let tmp = _.cloneDeep( selectedTempComponent )
                    if( !_.isEmpty( tmp.content )){
                      tmp.content = val
                    } else if( tmp.children?.length > 0 ) {
                      tmp.children[0].content = val
                    }
                    updateBottomButtonText( val )
                    onChangeTempComponent( 'selectedTempComponent', tmp ) 
                  }} />
              </Col>
              <Col lg={ 7 }>
                <LinkInput 
                  selectedContainer={ redirectInfo }
                  onUpdate={ ( updatedDom ) => onChangeRedirectInfo( updatedDom ) }
                  setMediaDatabase={ ( isOpen ) => {
                    toggleMediaDatabase( isOpen )
                  }}
                  selectedLanguage={ selectedLanguage }
                />
              </Col>
            </Row>
            <FormGroup>
              <div className="d-flex align-items-center mb-1">
                <Label className="m-0">{ Lang[ 'STYLE' ][ selectedLanguage ] }</Label>
                {
                  !_.isEqual( selectedButtonTemplate.style, selectedTempComponent.style ) && (
                    <>
                      <IoMdRefresh 
                        id='reset_button_style'
                        style={{ cursor: 'pointer', marginLeft: '5px', fontSize: '18px'  }}
                        onClick={()=>{
                          let tmp = _.cloneDeep( selectedTempComponent )
                          tmp.style = selectedButtonTemplate.style
                          onChangeTempComponent( 'selectedTempComponent', tmp )
                        }}/>
                        <UncontrolledTooltip
                          target='reset_button_style'
                          placement="top">
                          { Lang[ 'RESET' ][ selectedLanguage ] }
                        </UncontrolledTooltip>
                      </>
                  )
                }
              </div>
              <Accordion>
                <Card style={{ overflow: "visible" }}>
                  <Accordion.Toggle
                    eventKey="button_color"
                    as={Card.Header}>
                    { Lang[ 'BUTTON_COLOR' ][ selectedLanguage ] }
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="button_color">
                    <Card.Body>
                      <Row>
                        { ColorPicker( 'color' ) }
                        { ColorPicker( selectedTempComponent.style.background?.indexOf( '#' ) > -1 ? 'background' : 'backgroundColor' ) }
                      </Row>
                      <Row>
                        { ColorPicker( 'outline' ) }
                        { Object.keys( getBorderStyling( selectedTempComponent.style )).length > 0 && ColorPicker( 'border' ) }
                      </Row>  
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
                <Card style={{ overflow: "visible" }}>
                  <Accordion.Toggle
                    eventKey="button_hover_color"
                    as={Card.Header}>
                    { Lang[ 'BUTTON_HOVER_COLOR' ][ selectedLanguage ] }
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey="button_hover_color">
                    <Card.Body>
                      <Row>
                        { ColorPicker( 'color', true ) }
                        { ColorPicker( selectedTempComponent.style.background?.indexOf( '#' ) > -1 ? 'background' : 'backgroundColor', true ) }
                      </Row>
                      <Row>
                        { ColorPicker( 'outline', true ) }
                        { Object.keys( getBorderStyling( selectedTempComponent.style[ ':hover' ] )).length > 0 && ColorPicker( 'border', true ) }
                      </Row>  
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </FormGroup>
          </ModalBody>
        )
      }
      <ModalFooter>
        <Button
          onClick={ () => onChangeTempComponent( 'showSelectButtonModal', false ) }>
          { Lang[ 'CLOSE' ][ selectedLanguage ] }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ButtonConfigModal